import { atom } from "jotai";

/**
 * Bumped to request opening the calendar's chat/threads side panel (the `g t`
 * shortcut). The calendar is the app home, so "go to threads" focuses the panel
 * there rather than navigating to a separate page. Calendar watches this counter
 * and opens the panel when it changes.
 */
export const openThreadsPanelSignalAtom = atom(0);

/**
 * A request to start a new chat. Bump it; the page decides what that means.
 *
 * A signal rather than an `open` boolean: composing is a canvas view on desktop,
 * so what the canvas shows is already held by the page's `activePivot`, and a
 * second boolean would be a rival answer to the same question. A trigger asks;
 * the page routes it to the pivot on desktop, or to the dialog on mobile, which
 * has no canvas column to be a view of.
 *
 * Several triggers, one destination: the nav row, the wordmark, the canvas bar
 * on the other views, the shortcuts, and the `?input=` deep link.
 */
export const newChatSignalAtom = atom(0);

/**
 * The last prompt sent from the new-chat composer this session. ↑ in that
 * composer, while it is empty, brings it back so a follow-up starts from what
 * was just asked rather than from a blank field (BIG-8149) — the shell-history
 * idea, at the size of one entry. Sent text only: a draft the user abandoned
 * is not a message they want back.
 */
export const lastNewChatPromptAtom = atom("");

const newChatSendCountAtom = atom(0);
const lastRememberedSendAtom = atom(0);

/**
 * Take a ticket for a send that is starting. Sends finish out of order — an
 * attachment upload can outlast a text-only send started after it — and the
 * ticket is what lets the record below keep the newest send rather than the
 * one that happened to finish last.
 */
export const beginNewChatSendAtom = atom(null, (get, set) => {
  const ticket = get(newChatSendCountAtom) + 1;
  set(newChatSendCountAtom, ticket);
  return ticket;
});

/**
 * Record a prompt once its send has succeeded. Blank text is not a prompt — a
 * file sent on its own has nothing to recall, and must not wipe out the last
 * thing that did — and a send older than the one already recorded is not the
 * latest, however late it finished.
 */
export const rememberNewChatPromptAtom = atom(
  null,
  (get, set, sent: { text: string; ticket: number }) => {
    if (!sent.text.trim() || sent.ticket < get(lastRememberedSendAtom)) return;
    set(lastRememberedSendAtom, sent.ticket);
    set(lastNewChatPromptAtom, sent.text);
  },
);
